import '@reach/dialog/styles.css'
import './appkit'
import './index.css'

import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import StepLineContextProvider from 'components/StepLine/context/StepLineContext'
import { SupportedChainId } from 'config/chains'
import { DEFAULT_WAGMI_REFETCH_POLICY } from 'config/RefetchPolicies'
import { FINAL_SUBGRAPH_URLS_V2, FINAL_SUBGRAPH_URLS_V3 } from 'config/subgraph' // Import the final URL maps
import { useConnection } from 'hooks/useConnection'
import { TOKEN_PRICES_QUERY_KEY } from 'hooks/useTokenPrice'
import { TOKEN_LIST_BY_VOLUME_QUERY_KEY, TRENDING_TOKENS_QUERY_KEY } from 'hooks/useTrendingTokens'
import { CURRENCY_BALANCE_QUERY_KEY } from 'lib/hooks/useCurrencyBalance'
import { persistQueries } from 'lib/utils/persistQueries'
import { StrictMode, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { HashRouter } from 'react-router-dom'
import RadialGradientByChainUpdater from 'theme/RadialGradientByChainUpdater'
import { WagmiProvider } from 'wagmi'

import Blocklist from './components/Blocklist'
import { wagmiAdapter } from './config/wagmi'
import App from './pages/App'
// Initialize with default providers
import store from './state'
import ApplicationUpdater from './state/application/updater'
import ListsUpdater from './state/lists/updater'
import TokenSelectorUpdater from './state/tokenSelector/updater'
import TransactionUpdater from './state/transactions/updater'
import ThemeProvider, { ThemedGlobalStyle } from './theme'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      ...DEFAULT_WAGMI_REFETCH_POLICY,
    },
  },
})
persistQueries(queryClient, [
  // Balances of the active account only: other wallets' holdings never linger on this device.
  { prefix: CURRENCY_BALANCE_QUERY_KEY, maxEntries: 2_000, groupBy: ([, , account]) => account },
  { prefix: TOKEN_PRICES_QUERY_KEY, maxEntries: 5 },
  { prefix: TOKEN_LIST_BY_VOLUME_QUERY_KEY, maxEntries: 5 },
  { prefix: TRENDING_TOKENS_QUERY_KEY, maxEntries: 5 },
])

const VITE_PRELOAD_RETRY_STORAGE_KEY = 'kodiak:vite-preload-retry-at'
const VITE_PRELOAD_RETRY_WINDOW_MS = 30_000

if (window.ethereum) {
  window.ethereum.autoRefreshOnNetworkChange = false
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()

  const lastRetryAt = Number(window.sessionStorage.getItem(VITE_PRELOAD_RETRY_STORAGE_KEY) ?? 0)
  const now = Date.now()

  if (now - lastRetryAt < VITE_PRELOAD_RETRY_WINDOW_MS) {
    return
  }

  window.sessionStorage.setItem(VITE_PRELOAD_RETRY_STORAGE_KEY, `${now}`)
  window.location.reload()
})

function Updaters() {
  return (
    <>
      <RadialGradientByChainUpdater />
      <ListsUpdater />
      <ApplicationUpdater />
      <TransactionUpdater />
      <TokenSelectorUpdater />
    </>
  )
}

const apolloClients = {
  v3: Object.entries(FINAL_SUBGRAPH_URLS_V3).reduce((acc, [chainId, url]) => {
    acc[chainId] = new ApolloClient({
      uri: url,
      cache: new InMemoryCache(),
    })
    return acc
  }, {} as { [chainId: string]: ApolloClient<any> }),

  v2: Object.entries(FINAL_SUBGRAPH_URLS_V2).reduce((acc, [chainId, url]) => {
    acc[chainId] = new ApolloClient({
      uri: url,
      cache: new InMemoryCache(),
    })
    return acc
  }, {} as { [chainId: string]: ApolloClient<any> }),
}

const defaultApolloClient = apolloClients.v3[SupportedChainId.BERA]

function ApolloProviderForCurrentChain({ children }: { children: any }) {
  const { chainId } = useConnection()
  const client = useMemo(() => {
    if (!chainId || !(chainId in apolloClients.v3)) {
      return defaultApolloClient
    }
    return apolloClients['v3'][chainId as keyof (typeof apolloClients)['v3']]
  }, [chainId])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export const useSubgraphClient = (subgraphVersion: 'v2' | 'v3') => {
  const { chainId } = useConnection()

  return useMemo(() => {
    const subgraphClients = apolloClients[subgraphVersion]
    if (!chainId || !subgraphClients || !(chainId in subgraphClients)) {
      return defaultApolloClient // Define a default client for unknown scenarios
    }
    return subgraphClients[chainId as keyof typeof subgraphClients]
  }, [subgraphVersion, chainId])
}

const container = document.getElementById('root') as HTMLElement
createRoot(container).render(
  <StrictMode>
    <Provider store={store}>
      <WagmiProvider config={wagmiAdapter.wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <HashRouter>
            <ApolloProviderForCurrentChain>
              <Blocklist>
                <Updaters />
                <ThemeProvider>
                  <ThemedGlobalStyle />
                  <StepLineContextProvider>
                    <App />
                  </StepLineContextProvider>
                </ThemeProvider>
              </Blocklist>
            </ApolloProviderForCurrentChain>
          </HashRouter>
        </QueryClientProvider>
      </WagmiProvider>
    </Provider>
  </StrictMode>
)
